import { makeT, esc } from "../i18n";

const t = makeT("notifications");

/*
 * Cobuntu Notifications Widget v1
 *
 * Drop-in bell icon + unread badge for white-label community landing
 * pages. Click navigates to the user's hub (cobuntu's notifications
 * surface today); polls /api/users/me/notifications/unread-count every
 * 60s to keep the badge fresh.
 *
 * Mirrors the auth widget pattern (cobuntu-auth-widget.md): the click
 * opens an in-place drawer (mounted to document.body) rather than
 * navigating to /hub. Works the same on community-app + on every
 * landing that mounts the widget — no React or host-side wiring
 * required.
 *
 * Auth gating: unread API requires auth. data-require-auth="prompt"
 * (default) keeps the bell visible for logged-out users + bounces to
 * /login?next=/hub on click. "hide" suppresses entirely.
 *
 * ────────────────────────────────────────────────────────────────
 * Payload contract — keep in sync with @cobuntu/notifications-ui
 *
 * This file is the vanilla-JS renderer used on whitelabel landing
 * pages where React isn't available. The React equivalent for the
 * community-app drawer lives in
 *   https://github.com/cobuntuHello/cobuntu-notifications-ui
 *   src/components/NotificationRow.tsx
 *
 * BOTH render `notifications.payload` from the BE — the per-type
 * dispatch + grouping + actor resolution logic must stay aligned
 * between this file and the package. If you add a NEW notification
 * type to the BE, add the case branch HERE first (this file is the
 * canonical reference because the existing
 * `__tests__/notifications-drawer-type-coverage.test.ts` reads the
 * package source and asserts coverage parity), then port the same
 * branch into the package's NotificationRow.tsx.
 *
 * Cross-reference test (consumer-side):
 *   __tests__/notifications-drawer-messages.test.tsx mounts the
 *   React drawer (which renders via the package) and asserts the
 *   exact text for each of the 22 prisma `NotificationType` values.
 *   If you change a message here without updating the package, the
 *   test will pass but visitors will see two different copy
 *   variants depending on which surface they hit.
 * ────────────────────────────────────────────────────────────────
 */

// ─── Constants ────────────────────────────────────────────────────

const COOKIE_NAME = "cobuntu_auth";
const MOUNT_SELECTOR = "[data-cobuntu-notifications]";
const MOUNTED_ATTR = "data-cobuntu-notifications-mounted";
const STYLE_ID = "cobuntu-notifications-widget-styles";
const POLL_INTERVAL_MS = 60_000;
const EXP_SKEW_MS = 30_000;
const NAV_TARGET = "/hub";

import { authedFetch } from "../api/authedFetch";
import { appNav } from "../shared/origin";
import { getCachedSession, primeSession } from "../shared/session";

declare const __WIDGET_API_URL__: string;

/**
 * Absolute API base for UNAUTHENTICATED calls only — community lookup by
 * domain, publishable key. Those endpoints carry no session, so cross-origin
 * is fine and the widget may run on a customer landing where they are not
 * proxied.
 *
 * AUTHED calls must NOT use this. They go through `authedFetch`, which is
 * same-origin by design: the cobuntu_auth cookie is host-scoped, so an authed
 * cross-origin call arrives anonymous and every widget renders a guest. That
 * was a live outage — see api/authedFetch.ts.
 */
const API_URL: string =
  typeof __WIDGET_API_URL__ !== "undefined" ? __WIDGET_API_URL__ : "https://api.cobuntu.com";



// ─── Types ────────────────────────────────────────────────────────

interface NotificationRecord {
  id: string;
  type: string;
  status: "UNREAD" | "READ";
  payload: Record<string, unknown> | null;
  createdAt: string;
  readAt: string | null;
}

interface FriendRequestRecord {
  id: string;
  user: {
    id: string;
    name: string;
    usertag: string;
    profileImage: string | null;
  };
  sentAt: string;
}

// ─── State ────────────────────────────────────────────────────────

let unreadCount = 0;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let drawerEl: HTMLElement | null = null;
let backdropEl: HTMLElement | null = null;
let activeTab: "activity" | "requests" = "activity";
let notifications: NotificationRecord[] = [];
let requests: FriendRequestRecord[] = [];
let isFetching = false;

// ─── Cookie / auth ────────────────────────────────────────────────

/**
 * Is this visitor signed in? (app platform S3.)
 *
 * Reads the session the SERVER resolved instead of decoding the auth cookie
 * here. S5 makes that cookie httpOnly, so `document.cookie` returns nothing
 * and the old decode would conclude "guest" for every signed-in member on
 * three live customer sites, silently.
 *
 * Synchronous on purpose: mountAll() defers until the session is known, so by
 * the time any render path asks, the answer is cached. Keeping it sync is what
 * made this a small change rather than an async rewrite of the render flow.
 */
function isAuthenticated(): boolean {
  return getCachedSession()?.authenticated === true;
}

// ─── Unread fetch + poll ──────────────────────────────────────────

async function fetchUnread(): Promise<number> {
  if (!isAuthenticated()) return 0;
  try {
    // No authToken: this is a same-origin path, so the browser attaches the
    // cookie itself and the apex proxy forwards it server-side. That keeps
    // working once the cookie is httpOnly; a JS-read token would not.
    const res = await authedFetch(`/api/users/me/notifications/unread-count`);
    if (!res.ok) return unreadCount;
    const body = (await res.json()) as { data?: { count?: number }; count?: number };
    // Endpoint returns { data: { count } } in production; accept the flat
    // shape too for forward-compat with the planned v2 response simplification.
    const count = body.data?.count ?? body.count ?? 0;
    return typeof count === "number" ? count : 0;
  } catch {
    return unreadCount;
  }
}

async function refresh(): Promise<void> {
  const next = await fetchUnread();
  if (next === unreadCount) return;
  unreadCount = next;
  updateAllBadges();
}

function startPolling(): void {
  if (pollTimer) return;
  refresh();
  pollTimer = setInterval(() => {
    if (document.visibilityState === "visible") refresh();
  }, POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refresh();
  });
}

// ─── Slot pattern (Level 3) ───────────────────────────────────────

function hasCustomSlots(host: HTMLElement): boolean {
  return !!host.querySelector("[data-cobuntu-bind], [data-cobuntu-action]");
}

function applySlot(host: HTMLElement): void {
  host.querySelectorAll<HTMLElement>("[data-cobuntu-bind='unread-count']").forEach((el) => {
    el.textContent = unreadCount > 99 ? "99+" : String(unreadCount);
    el.style.display = unreadCount > 0 ? "" : "none";
  });
  host.querySelectorAll<HTMLElement>("[data-cobuntu-show='has-unread']").forEach((el) => {
    el.style.display = unreadCount > 0 ? "" : "none";
  });
  host.querySelectorAll<HTMLElement>("[data-cobuntu-show='no-unread']").forEach((el) => {
    el.style.display = unreadCount === 0 ? "" : "none";
  });
  host.querySelectorAll<HTMLElement>("[data-cobuntu-action='open-notifications']").forEach((el) => {
    if (el.getAttribute("data-cobuntu-bound") === "1") return;
    el.setAttribute("data-cobuntu-bound", "1");
    el.addEventListener("click", (e) => {
      e.preventDefault();
      navigateToNotifications();
    });
  });
}

// ─── Default render (Level 1 + 2) ─────────────────────────────────

function renderDefault(host: HTMLElement): void {
  const variant = host.getAttribute("data-variant") || "icon"; // icon | button
  const size = host.getAttribute("data-size") || "md";
  const showBadge = host.getAttribute("data-show-badge") !== "false";
  const ariaLabel = host.getAttribute("data-label") || "Notifications";
  const buttonLabel = host.getAttribute("data-button-label") || "Notifications";
  const requireAuth = host.getAttribute("data-require-auth") || "prompt";

  // Logged-out + hide → render nothing. Collapse the mount with
  // display:none so the empty <div> doesn't keep a slot in a flex/gap
  // header — that leftover slot surfaces as a stray gap between the
  // visible widgets (reported on PBN, 2026-06-09).
  if (!isAuthenticated() && requireAuth === "hide") {
    host.style.display = "none";
    host.innerHTML = "";
    return;
  }

  // A re-render (e.g. after sign-in) must undo any previous hide.
  host.style.display = "";
  host.innerHTML = "";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.setAttribute("aria-label", ariaLabel);
  btn.setAttribute("title", ariaLabel);
  // Anon variant: muted appearance + aria-disabled, click still
  // routes to /login. Same rationale as messages widget — keep the
  // affordance visible so the layout doesn't jump on auth, but make
  // it visually obvious it's not actionable in its usual sense
  // until the viewer signs in. Surfaced 2026-05-31 (Bela).
  const anon = !isAuthenticated();
  btn.className =
    `cobuntu-notif-trigger cobuntu-notif-trigger--${variant} cobuntu-notif-trigger--${size}` +
    (anon ? " cobuntu-notif-trigger--anon" : "");
  if (anon) btn.setAttribute("aria-disabled", "true");
  btn.innerHTML = `
    ${bellIconSvg()}
    ${variant === "button" ? `<span class="cobuntu-notif-trigger__label">${escapeHtml(buttonLabel)}</span>` : ""}
    ${showBadge && !anon ? `<span class="cobuntu-notif-trigger__badge" hidden>0</span>` : ""}
  `;
  btn.addEventListener("click", navigateToNotifications);
  host.appendChild(btn);
  updateBadgeOn(host);
}

function renderHost(host: HTMLElement): void {
  host.setAttribute(MOUNTED_ATTR, "");
  if (hasCustomSlots(host)) applySlot(host);
  else renderDefault(host);
}

function updateBadgeOn(host: HTMLElement): void {
  const badge = host.querySelector<HTMLElement>(".cobuntu-notif-trigger__badge");
  if (badge) {
    badge.textContent = unreadCount > 99 ? "99+" : String(unreadCount);
    if (unreadCount === 0) badge.setAttribute("hidden", "");
    else badge.removeAttribute("hidden");
  }
  if (hasCustomSlots(host)) applySlot(host);
}

function updateAllBadges(): void {
  document.querySelectorAll<HTMLElement>(MOUNT_SELECTOR).forEach(updateBadgeOn);
}

// ─── Navigation ───────────────────────────────────────────────────

function navigateToNotifications(): void {
  if (!isAuthenticated()) {
    location.href = appNav(`/login?next=${encodeURIComponent(NAV_TARGET)}`);
    return;
  }
  openDrawer();
}

// ─── Mount + DOM watch ────────────────────────────────────────────

function mountAll(): void {
  // Do not paint before the session is known (S3). renderHost marks each host
  // with MOUNTED_ATTR and mountAll skips anything already marked, so a render
  // done with an unknown session is not merely an early paint — it STICKS. A
  // signed-in member would keep the anon variant until a full reload.
  //
  // Deferring here rather than in init() covers the MutationObserver path too:
  // a host added later enters through the same door. primeSession() is
  // idempotent and cached, so re-entering costs nothing.
  if (getCachedSession() === null) {
    void primeSession().then(mountAll);
    return;
  }
  document
    .querySelectorAll<HTMLElement>(`${MOUNT_SELECTOR}:not([${MOUNTED_ATTR}])`)
    .forEach(renderHost);
}

let observer: MutationObserver | null = null;
function watchDom(): void {
  if (observer) return;
  observer = new MutationObserver(mountAll);
  observer.observe(document.body, { childList: true, subtree: true });
}

// ─── Helpers ──────────────────────────────────────────────────────

/**
 * Return an app-supplied notification link only if it is safe to navigate to.
 *
 * Vanilla twin of `safeNotificationUrl` in @cobuntu/notifications-ui. The two
 * MUST stay in lockstep — notifications-renderer-parity.test.ts is the guard —
 * but this bundle is standalone by design (it mounts on whitelabel landing
 * pages where the React package is not loaded), so the logic is duplicated
 * rather than imported.
 *
 * This matters more here than in the React row: the drawer navigates by
 * assigning `location.href` directly, so a `javascript:` URL reaching this
 * function's caller would execute script on a customer's own domain. The
 * backend write endpoint rejects the same set, making this the second of two
 * gates; it exists because notification payloads are free-form JSON and this
 * renderer cannot verify which writer produced a given row.
 *
 * Accepts https, http, and single-slash site-relative paths. Refuses
 * protocol-relative `//host`, which reads like a path but navigates off-origin.
 */
function safeAppUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const url = raw.trim();
  if (!url) return null;

  if (url.startsWith("/")) {
    return url.startsWith("//") ? null : url;
  }

  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default: return "&#39;";
    }
  });
}

// Ported from cobuntu-frontend/src/utils/time.ts
function formatTimeAgo(iso: string): string {
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "";
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  const months = Math.max(1, Math.floor(days / 30));
  if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years > 1 ? "s" : ""} ago`;
}

// Ported from cobuntu-frontend/src/features/notifications/utils/groupNotifications.ts
function getTimeSection(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  if (date >= startOfToday) return "Today";
  if (date >= startOfYesterday) return "Yesterday";
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) return "This Week";
  if (diffDays < 30) return "This Month";
  return "Earlier";
}

// Ported from cobuntu-frontend/src/utils/cleanMentionMarkup.ts
function cleanMentionMarkup(content: string): string {
  if (!content) return content;
  return content
    .replace(/@\[([^\]]+)\]\(user:([^)]+)\)/g, "@$1")
    .replace(/\/\[([^\]]+)\]\(community:([^)]+)\)/g, "/$1");
}

function truncate(s: string, max = 80): string {
  if (!s || s.length <= max) return s;
  return s.substring(0, max).trim() + "...";
}
function formatPostSnippet(s?: string | null): string {
  if (!s) return "";
  return truncate(cleanMentionMarkup(s));
}

// Ported from cobuntu-frontend's renderActorNames — produces HTML-safe
// "Becky", "Becky and Ana", "Becky, Ana and 2 others", etc. Verified
// actors get a small checkmark icon inline.
function renderActorNames(actors: Array<{ name: string; isVerified?: boolean }>, maxNames = 2): string {
  if (actors.length === 0) return `<strong>${esc(t("someone", "Someone"))}</strong>`;
  const renderOne = (a: { name: string; isVerified?: boolean }) =>
    `<strong>${escapeHtml(a.name)}</strong>${a.isVerified ? verifiedBadgeSvg() : ""}`;
  if (actors.length === 1) return renderOne(actors[0]);
  if (actors.length === 2) return `${renderOne(actors[0])} <span class="cobuntu-notif-row__muted">and</span> ${renderOne(actors[1])}`;
  if (actors.length <= maxNames + 1) {
    const leading = actors.slice(0, -1).map(renderOne).join(`<span class="cobuntu-notif-row__muted">, </span>`);
    return `${leading} <span class="cobuntu-notif-row__muted">and</span> ${renderOne(actors[actors.length - 1])}`;
  }
  const leading = actors.slice(0, maxNames).map(renderOne).join(`<span class="cobuntu-notif-row__muted">, </span>`);
  const remaining = actors.length - maxNames;
  return `${leading} <span class="cobuntu-notif-row__muted">and ${remaining} other${remaining > 1 ? "s" : ""}</span>`;
}

function verifiedBadgeSvg(): string {
  return `<svg viewBox="0 0 24 24" width="11" height="11" fill="#3b82f6" aria-hidden="true" style="display:inline-block;vertical-align:middle;margin-left:2px;"><path d="M12 2l2.2 3 3.6.4 1.4 3.4-1.7 3.2 1.7 3.2-1.4 3.4-3.6.4-2.2 3-2.2-3-3.6-.4-1.4-3.4 1.7-3.2-1.7-3.2 1.4-3.4 3.6-.4z"/><path d="M8.5 12.5l2.5 2.5 4.5-4.5" stroke="#fff" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

// ─── Data fetch ───────────────────────────────────────────────────

async function fetchNotificationsList(): Promise<NotificationRecord[]> {
  try {
    // No authToken: this is a same-origin path, so the browser attaches the
    // cookie itself and the apex proxy forwards it server-side. That keeps
    // working once the cookie is httpOnly; a JS-read token would not.
    const res = await authedFetch(`/api/users/me/notifications?limit=50`);
    if (!res.ok) return [];
    const body = (await res.json()) as { data?: NotificationRecord[] };
    return Array.isArray(body.data) ? body.data : [];
  } catch {
    return [];
  }
}

async function fetchFriendRequests(): Promise<FriendRequestRecord[]> {
  try {
    // No authToken: this is a same-origin path, so the browser attaches the
    // cookie itself and the apex proxy forwards it server-side. That keeps
    // working once the cookie is httpOnly; a JS-read token would not.
    const res = await authedFetch(`/api/users/me/friend-requests/incoming`);
    if (!res.ok) return [];
    const body = (await res.json()) as { requests?: FriendRequestRecord[] };
    return Array.isArray(body.requests) ? body.requests : [];
  } catch {
    return [];
  }
}

async function markAllRead(): Promise<void> {
  if (!isAuthenticated()) return;
  try {
    await authedFetch(`/api/users/me/notifications/mark-read`, {
      method: "PATCH",
    });
    unreadCount = 0;
    updateAllBadges();
  } catch {
    /* non-fatal — next poll will reconcile */
  }
}

async function acceptFriendRequest(usertag: string): Promise<boolean> {
  try {
    // No authToken: this is a same-origin path, so the browser attaches the
    // cookie itself and the apex proxy forwards it server-side. That keeps
    // working once the cookie is httpOnly; a JS-read token would not.
    const res = await authedFetch(
      `/api/users/me/friends/${encodeURIComponent(usertag)}/accept`,
      { method: "POST" },
    );
    return res.ok;
  } catch {
    return false;
  }
}

async function rejectFriendRequest(usertag: string): Promise<boolean> {
  try {
    // No authToken: this is a same-origin path, so the browser attaches the
    // cookie itself and the apex proxy forwards it server-side. That keeps
    // working once the cookie is httpOnly; a JS-read token would not.
    const res = await authedFetch(
      `/api/users/me/friends/${encodeURIComponent(usertag)}/reject`,
      { method: "DELETE" },
    );
    return res.ok;
  } catch {
    return false;
  }
}

// ─── Notification formatter ───────────────────────────────────────
// Maps the BE's polymorphic { type, payload } into a flat display
// shape. Payload field names are taken directly from the BE notification
// builders (services/notifications + services/core/.../friendshipsService.ts)
// and from the legacy cobuntu-frontend's NotificationsPanel.tsx which
// rendered these correctly before the rewrite. Unknown types render
// with a generic fallback so a new backend type rolling out doesn't
// break the drawer.

type Overlay = "heart" | "check" | "star" | "x" | "people" | "shield" | null;
/** Solid-color icon (no avatar) — listing types + system events. */
type SolidIcon =
  | "package"        // LISTING_REQUESTED
  | "check-circle"   // LISTING_APPROVED, LISTING_PROPOSAL_ACCEPTED
  | "x-circle"       // LISTING_REJECTED, LISTING_PROPOSAL_REJECTED
  | "ban"            // LISTING_DEACTIVATED
  | "arrow-left"     // LISTING_WITHDRAWN
  | "coins"          // LISTING_PROPOSAL
  | "star"           // POST_STAR_ACHIEVED
  | "shield"         // POST_REMOVED_FROM_COMMUNITY
  | "user-x"         // MEMBERSHIP_KICKED_USER
  | null;

interface ActorView {
  name: string;
  usertag?: string | null;
  imageUrl: string | null;
  initial: string;
  isVerified?: boolean;
}

interface DisplayNotification {
  id: string;
  /** Pre-rendered HTML for the message text (already escaped + may
   *  contain inline <strong> + verified badges). */
  messageHtml: string;
  snippet: string | null;
  /** Multi-actor avatars when the row is grouped ("Becky and Ana
   *  reacted to your post"). Empty when using a solid icon instead. */
  actors: ActorView[];
  /** Solid-color icon shown INSTEAD of avatars for system events
   *  (listings, kicked, star, etc.). null when avatars are used. */
  solidIcon: SolidIcon;
  /** Solid-icon background tint (matches legacy: orange/green/red/yellow/blue). */
  solidTone: "orange" | "green" | "red" | "yellow" | "blue" | "neutral";
  /** Small badge overlaid on the avatar bottom-right. */
  overlay: Overlay;
  /** Optional post thumbnail rendered on the right side (44×44). */
  thumbnail: string | null;
  href: string | null;
  createdAt: string;
  isUnread: boolean;
}

interface ActorRef {
  name?: string;
  usertag?: string;
  avatarUrl?: string;
  profileImage?: string;
}

interface CommunityRef {
  name?: string;
  communityTag?: string;
  iconUrl?: string;
}

function actorInitial(name: string | undefined | null): string {
  if (!name) return "?";
  const trimmed = name.trim();
  return trimmed ? trimmed[0].toUpperCase() : "?";
}

/**
 * Reads "the user this notification is about" from the payload. The BE
 * uses different keys depending on the notification's direction:
 *   - fromUser   → someone is acting ON the viewer (request, mention)
 *   - byUser     → someone is being told ABOUT another user (e.g.
 *                  "X accepted your friend request")
 *   - author     → post author for POST_* notifications
 *   - mentioner* → flat fields for FEED_MENTIONED (mentionerName etc.)
 * We probe all of these in priority order.
 */
function pickActor(p: Record<string, unknown>): ActorRef {
  const fromUser = (p.fromUser as ActorRef) || null;
  const byUser = (p.byUser as ActorRef) || null;
  const author = (p.author as ActorRef) || null;
  const mentioner = (p.mentioner as ActorRef) || null;
  // FEED_MENTIONED uses flat keys.
  const flatMention: ActorRef | null =
    p.mentionerName || p.mentionerUsertag || p.mentionerImage
      ? {
          name: p.mentionerName as string | undefined,
          usertag: p.mentionerUsertag as string | undefined,
          avatarUrl: p.mentionerImage as string | undefined,
        }
      : null;
  // Free-form named-field fallbacks (POST_REACTED uses reactorName, etc.)
  const namedFallback: ActorRef | null =
    p.reactorName || p.likerName || p.commenterName || p.replierName || p.requesterName
      ? {
          name:
            (p.reactorName as string) ||
            (p.likerName as string) ||
            (p.commenterName as string) ||
            (p.replierName as string) ||
            (p.requesterName as string) ||
            undefined,
        }
      : null;
  return fromUser || byUser || author || mentioner || flatMention || namedFallback || {};
}

function actorAvatar(actor: ActorRef): string | null {
  return actor.avatarUrl || actor.profileImage || null;
}

function pickCommunity(p: Record<string, unknown>): CommunityRef {
  return (
    (p.fromCommunity as CommunityRef) ||
    (p.community as CommunityRef) ||
    {}
  );
}

function communityName(c: CommunityRef): string {
  return c.name || "the community";
}

// ─── Grouping (ported from
//     cobuntu-frontend/src/features/notifications/utils/groupNotifications.ts)
//
// Multiple notifications of the same POST_* type on the same post
// collapse into "Becky, Ana and 2 others reacted to your post".

const GROUPABLE_TYPES = new Set([
  "POST_REACTED",
  "POST_COMMENTED",
  "POST_COMMENT_REPLY",
  "POST_COMMENT_LIKED",
]);

interface GroupedRow {
  isGroup: true;
  id: string;
  groupType: string;
  notifications: NotificationRecord[];
  actors: ActorView[];
  avatarActors: ActorView[]; // first two for the stacked avatar display
  createdAt: string;
}

type DisplayItem = NotificationRecord | GroupedRow;

function extractGroupActor(n: NotificationRecord): ActorView {
  const p = (n.payload ?? {}) as Record<string, unknown>;
  switch (n.type) {
    case "POST_REACTED":
      return actorToView({
        name: (p.reactorName as string) || (p.likerName as string),
        usertag: (p.reactorUsertag as string) || (p.likerUsertag as string),
      });
    case "POST_COMMENTED":
      return actorToView({
        name: p.commenterName as string,
        usertag: p.commenterUsertag as string,
      });
    case "POST_COMMENT_REPLY":
      return actorToView({
        name: p.replierName as string,
        usertag: p.replierUsertag as string,
      });
    case "POST_COMMENT_LIKED":
      return actorToView({
        name: p.likerName as string,
        usertag: p.likerUsertag as string,
      });
    default:
      return actorToView(pickActor(p));
  }
}

function groupNotifications(records: NotificationRecord[]): DisplayItem[] {
  const groupKey = (n: NotificationRecord): string | null => {
    if (!GROUPABLE_TYPES.has(n.type)) return null;
    const postId = (n.payload as Record<string, unknown> | null)?.postId;
    if (typeof postId !== "string") return null;
    return `${n.type}_${postId}`;
  };

  const groups = new Map<string, NotificationRecord[]>();
  const placeholders: Array<{ key: string; index: number } | null> = [];
  const result: DisplayItem[] = [];

  for (const n of records) {
    const key = groupKey(n);
    if (key) {
      if (!groups.has(key)) {
        groups.set(key, []);
        placeholders.push({ key, index: result.length });
        result.push(n); // placeholder, replaced below
      } else {
        placeholders.push(null);
      }
      groups.get(key)!.push(n);
    } else {
      placeholders.push(null);
      result.push(n);
    }
  }

  for (const [key, group] of groups) {
    const idx = placeholders.find((p) => p?.key === key)?.index;
    if (idx === undefined) continue;
    if (group.length === 1) {
      result[idx] = group[0];
      continue;
    }
    const latest = group[0];
    const seen = new Set<string>();
    const actors: ActorView[] = [];
    for (const n of group) {
      const a = extractGroupActor(n);
      if (!seen.has(a.name)) {
        seen.add(a.name);
        actors.push(a);
      }
    }
    result[idx] = {
      isGroup: true,
      id: `group_${key}`,
      groupType: latest.type,
      notifications: group,
      actors,
      avatarActors: actors.slice(0, 2),
      createdAt: latest.createdAt,
    };
  }
  return result;
}

function isGroup(item: DisplayItem): item is GroupedRow {
  return (item as GroupedRow).isGroup === true;
}

function formatItem(item: DisplayItem): DisplayNotification {
  if (isGroup(item)) {
    return formatNotification(item.notifications[0], item);
  }
  return formatNotification(item);
}

/** Turn an ActorRef into the renderable ActorView (with avatar + initial). */
function actorToView(actor: ActorRef): ActorView {
  const name = actor.name || "Someone";
  return {
    name,
    usertag: actor.usertag || null,
    imageUrl: actorAvatar(actor),
    initial: actorInitial(name),
    isVerified: false,
  };
}

/** Wrap a name in <strong> + optional verified badge. Used for messages
 *  where there's exactly one actor (e.g. "Bob commented on your post"). */
function nameSpan(actor: ActorView): string {
  return `<strong>${escapeHtml(actor.name)}</strong>${actor.isVerified ? verifiedBadgeSvg() : ""}`;
}
function muted(s: string): string {
  return `<span class="cobuntu-notif-row__muted">${escapeHtml(s)}</span>`;
}

function formatNotification(n: NotificationRecord, group?: GroupedRow): DisplayNotification {
  const p = (n.payload ?? {}) as Record<string, unknown>;
  const isUnread = n.status === "UNREAD";
  const actor = pickActor(p);
  const actorView = actorToView({ ...actor, name: actor.name || (group?.actors[0]?.name ?? undefined) });
  const community = pickCommunity(p);

  const baseHref = actorView.usertag ? `/members/${actorView.usertag}` : null;
  const communityHref = community.communityTag ? `/` : null;
  const postThumb = (p.postMediaUrl as string | undefined) || null;
  // Single post pages now live at /feed/post/<postId> (community-app
  // route /(community)/feed/post/[postId]). The legacy /posts/<id>
  // URL pattern was retired with services/posts; /feed/[postId] is a
  // redirect-to-/feed shim for old shared links — that's NOT the page
  // we want clicks to land on. Use messageId for FEED_MENTIONED
  // (chapter post in the feed) and postId for POST_*.
  const postId =
    (p.postId as string | undefined) ||
    (p.messageId as string | undefined) ||
    null;
  const postHref = postId ? `/feed/post/${postId}` : null;

  // Common helper to assemble a row with avatar(s).
  const avatarRow = (over: {
    messageHtml: string;
    snippet?: string | null;
    actors?: ActorView[];
    overlay?: Overlay;
    href?: string | null;
    thumbnail?: string | null;
  }): DisplayNotification => ({
    id: n.id,
    createdAt: n.createdAt,
    messageHtml: over.messageHtml,
    snippet: over.snippet ?? null,
    actors: over.actors ?? [actorView],
    solidIcon: null,
    solidTone: "neutral",
    overlay: over.overlay ?? null,
    thumbnail: over.thumbnail ?? null,
    href: over.href ?? null,
    isUnread,
  });

  // Helper for solid-icon rows (listings, system events).
  const iconRow = (over: {
    messageHtml: string;
    snippet?: string | null;
    solidIcon: SolidIcon;
    solidTone: DisplayNotification["solidTone"];
    href?: string | null;
    thumbnail?: string | null;
  }): DisplayNotification => ({
    id: n.id,
    createdAt: n.createdAt,
    messageHtml: over.messageHtml,
    snippet: over.snippet ?? null,
    actors: [],
    solidIcon: over.solidIcon,
    solidTone: over.solidTone,
    overlay: null,
    thumbnail: over.thumbnail ?? null,
    href: over.href ?? null,
    isUnread,
  });

  switch (n.type) {
    case "FRIEND_REQUEST_INBOUND":
      return avatarRow({
        messageHtml: `${nameSpan(actorView)} ${muted("sent you a friend request")}`,
        href: baseHref,
      });

    case "FRIEND_REQUEST_ACCEPTED":
      return avatarRow({
        messageHtml: `${nameSpan(actorView)} ${muted("accepted your friend request")}`,
        href: baseHref,
      });

    case "FRIENDSHIP_CONFIRMED":
      return avatarRow({
        messageHtml: `${nameSpan(actorView)} ${muted("is now friends with you")}`,
        href: baseHref,
      });

    case "MEMBERSHIP_CONFIRMED": {
      const fromCommunity = (p.fromCommunity as CommunityRef) || null;
      const byUser = (p.byUser as ActorRef) || null;
      if (fromCommunity) {
        const communityActor: ActorView = {
          name: fromCommunity.name || "the community",
          imageUrl: fromCommunity.iconUrl || null,
          initial: actorInitial(fromCommunity.name),
        };
        return avatarRow({
          messageHtml: `${muted("You joined")} <strong>${escapeHtml(fromCommunity.name || "")}</strong>`,
          actors: [communityActor],
          overlay: "check",
          href: communityHref,
        });
      }
      if (byUser && community.name) {
        const byView = actorToView(byUser);
        const communityActor: ActorView = {
          name: community.name,
          imageUrl: community.iconUrl || null,
          initial: actorInitial(community.name),
        };
        return avatarRow({
          messageHtml: `${nameSpan(byView)} ${muted("joined")} <strong>${escapeHtml(community.name)}</strong>`,
          actors: [communityActor],
          overlay: "check",
          href: communityHref,
        });
      }
      return iconRow({
        messageHtml: muted("Your membership was confirmed."),
        solidIcon: "check-circle",
        solidTone: "green",
      });
    }

    case "MEMBERSHIP_REQUEST":
      return avatarRow({
        messageHtml: `${nameSpan(actorView)} ${muted("requested to join")} <strong>${escapeHtml(community.name || "")}</strong>`,
        href: community.communityTag ? `/members` : baseHref,
        overlay: "people",
      });

    case "MEMBERSHIP_KICKED_USER":
      return iconRow({
        messageHtml: `${muted("You were removed from")} <strong>${escapeHtml(community.name || "the community")}</strong>`,
        solidIcon: "user-x",
        solidTone: "red",
      });

    case "FEED_MENTIONED":
      return avatarRow({
        messageHtml: `${nameSpan(actorView)} ${muted("mentioned you")}`,
        snippet: formatPostSnippet(p.text as string | undefined),
        // postHref already maps to /feed/post/<messageId> when the
        // payload includes one (the BE stamps messageId on
        // FEED_MENTIONED). Falls back to the feed home otherwise.
        href: postHref ?? "/feed",
        thumbnail: postThumb,
      });

    case "POST_REACTED":
      return avatarRow({
        messageHtml: group
          ? `${renderActorNames(group.actors)} ${muted("reacted to your post")}`
          : `${nameSpan(actorView)} ${muted("reacted to your post")}`,
        snippet: formatPostSnippet(p.postSnippet as string | undefined),
        actors: group ? group.avatarActors : [actorView],
        overlay: "heart",
        href: postHref,
        thumbnail: postThumb,
      });

    case "POST_COMMENTED":
      return avatarRow({
        messageHtml: group
          ? `${renderActorNames(group.actors)} ${muted("commented on your post")}`
          : `${nameSpan(actorView)} ${muted("commented on your post")}`,
        snippet: formatPostSnippet(p.commentSnippet as string | undefined),
        actors: group ? group.avatarActors : [actorView],
        href: postHref,
        thumbnail: postThumb,
      });

    case "POST_COMMENT_REPLY":
      return avatarRow({
        messageHtml: group
          ? `${renderActorNames(group.actors)} ${muted("replied to your comment")}`
          : `${nameSpan(actorView)} ${muted("replied to your comment")}`,
        snippet: formatPostSnippet(p.replySnippet as string | undefined),
        actors: group ? group.avatarActors : [actorView],
        href: postHref,
        thumbnail: postThumb,
      });

    case "POST_COMMENT_LIKED":
      return avatarRow({
        messageHtml: group
          ? `${renderActorNames(group.actors)} ${muted("liked your comment")}`
          : `${nameSpan(actorView)} ${muted("liked your comment")}`,
        snippet: formatPostSnippet(p.commentSnippet as string | undefined),
        actors: group ? group.avatarActors : [actorView],
        overlay: "heart",
        thumbnail: postThumb,
      });

    case "POST_MENTIONED":
      return avatarRow({
        messageHtml: `${nameSpan(actorView)} ${muted("mentioned you in a post")}`,
        snippet: formatPostSnippet(p.postText as string | undefined),
        href: postHref,
        thumbnail: postThumb,
      });

    case "POST_STAR_ACHIEVED": {
      const lvl = (p.starLevel as number | undefined) ?? 1;
      return iconRow({
        messageHtml: `${muted("Your post reached")} <strong style="color:#eab308">${lvl} star${lvl === 1 ? "" : "s"}!</strong>`,
        solidIcon: "star",
        solidTone: "yellow",
        thumbnail: postThumb,
      });
    }

    case "POST_REMOVED_FROM_COMMUNITY":
      return iconRow({
        messageHtml: `${muted("Your post was removed from")} <strong>${escapeHtml(community.name || (p.communityName as string) || "the community")}</strong>`,
        solidIcon: "shield",
        solidTone: "red",
        thumbnail: postThumb,
      });

    // ─── Listings ──────────────────────────────────────────────────
    case "LISTING_REQUESTED":
      return iconRow({
        messageHtml: `${nameSpan(actorToView({ name: (p.requesterName as string) || "Someone" }))} ${muted("requested to list")} <strong>${escapeHtml((p.itemName as string) || "")}</strong>`,
        solidIcon: "package",
        solidTone: "orange",
      });
    case "LISTING_APPROVED":
      return iconRow({
        messageHtml: `${muted("Your")} ${(p.itemType as string) || "listing"} <strong>${escapeHtml((p.itemName as string) || "")}</strong> ${muted("was approved in")} <strong>${escapeHtml((p.communityName as string) || "")}</strong>`,
        solidIcon: "check-circle",
        solidTone: "green",
      });
    case "LISTING_REJECTED":
      return iconRow({
        messageHtml: `${muted("Your")} ${(p.itemType as string) || "listing"} <strong>${escapeHtml((p.itemName as string) || "")}</strong> ${muted("was declined in")} <strong>${escapeHtml((p.communityName as string) || "")}</strong>`,
        solidIcon: "x-circle",
        solidTone: "red",
      });
    case "LISTING_DEACTIVATED":
      return iconRow({
        messageHtml: `${muted("Your")} ${(p.itemType as string) || "listing"} <strong>${escapeHtml((p.itemName as string) || "")}</strong> ${muted("was deactivated in")} <strong>${escapeHtml((p.communityName as string) || "")}</strong>`,
        solidIcon: "ban",
        solidTone: "red",
      });
    case "LISTING_WITHDRAWN":
      return iconRow({
        messageHtml: `${nameSpan(actorToView({ name: (p.ownerName as string) || "Someone" }))} ${muted("withdrew")} <strong>${escapeHtml((p.itemName as string) || "")}</strong>`,
        solidIcon: "arrow-left",
        solidTone: "yellow",
      });
    case "LISTING_PROPOSAL":
      return iconRow({
        messageHtml: `${muted("New commission proposal for")} <strong>${escapeHtml((p.itemName as string) || "")}</strong>`,
        solidIcon: "coins",
        solidTone: "blue",
      });
    case "LISTING_PROPOSAL_ACCEPTED":
      return iconRow({
        messageHtml: `${muted("Commission agreed for")} <strong>${escapeHtml((p.itemName as string) || "")}</strong>`,
        solidIcon: "check-circle",
        solidTone: "green",
      });
    case "LISTING_PROPOSAL_REJECTED":
      return iconRow({
        messageHtml: `${muted("Commission proposal declined for")} <strong>${escapeHtml((p.itemName as string) || "")}</strong>`,
        solidIcon: "x-circle",
        solidTone: "red",
      });

    // App platform P2.6. The only type whose copy an installed app authors
    // rather than the platform, so title / body / url are untrusted input:
    // escaped on render, and the link put through safeAppUrl before it can
    // become the row's href (the drawer navigates by assigning location.href,
    // so an unvalidated javascript: URL here would be script execution on a
    // whitelabel landing page).
    case "APP_NOTIFICATION": {
      const appName = (p.appName as string) || "";
      const title = (p.title as string) || "";
      return iconRow({
        messageHtml: `<strong>${escapeHtml(title)}</strong>${appName ? ` ${muted(`· ${escapeHtml(appName)}`)}` : ""}`,
        snippet: (p.body as string) || null,
        solidIcon: "package",
        solidTone: "blue",
        href: safeAppUrl(p.url),
      });
    }

    default:
      return iconRow({
        messageHtml: muted(n.type),
        solidIcon: null,
        solidTone: "neutral",
      });
  }
}

function overlaySvg(kind: Overlay): string {
  switch (kind) {
    case "heart":
      return `<svg viewBox="0 0 24 24" width="10" height="10" fill="#fff" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/></svg>`;
    case "check":
      return `<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
    case "star":
      return `<svg viewBox="0 0 24 24" width="10" height="10" fill="#fff" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>`;
    case "x":
      return `<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
    case "people":
      return `<svg viewBox="0 0 24 24" width="10" height="10" fill="#fff" aria-hidden="true"><circle cx="9" cy="8" r="3"/><path d="M2 21v-1a5 5 0 0 1 10 0v1zM17 7a3 3 0 1 1 0 6 3 3 0 0 1 0-6zM14 21v-1a4 4 0 0 1 8 0v1"/></svg>`;
    case "shield":
      return `<svg viewBox="0 0 24 24" width="10" height="10" fill="#fff" aria-hidden="true"><path d="M12 2l8 4v6c0 4.5-3.5 8.5-8 10-4.5-1.5-8-5.5-8-10V6l8-4z"/></svg>`;
    default:
      return "";
  }
}

function overlayBg(kind: Overlay): string {
  switch (kind) {
    case "heart":
    case "x":
    case "shield":
      return "#ef4444";
    case "check":
      return "#10b981";
    case "star":
      return "#eab308";
    case "people":
      return "var(--cobuntu-badge-bg, #d4af37)";
    default:
      return "transparent";
  }
}

// Solid-color icons used INSTEAD of avatars for non-actor system events.
function solidIconSvg(kind: SolidIcon): string {
  switch (kind) {
    case "package":
      return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16.5 9.4l-9-5.19"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>`;
    case "check-circle":
      return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>`;
    case "x-circle":
      return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>`;
    case "ban":
      return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>`;
    case "arrow-left":
      return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 8 8 12 12 16"/><line x1="16" y1="12" x2="8" y2="12"/></svg>`;
    case "coins":
      return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/><path d="M16.71 13.88l.7.71-2.82 2.82"/></svg>`;
    case "star":
      return `<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" stroke="currentColor" stroke-width="1" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;
    case "shield":
      return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`;
    case "user-x":
      return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="18" y1="8" x2="23" y2="13"/><line x1="23" y1="8" x2="18" y2="13"/></svg>`;
    default:
      return "";
  }
}

function solidIconToneStyles(tone: DisplayNotification["solidTone"]): { bg: string; color: string } {
  switch (tone) {
    case "orange":
      return { bg: "rgba(249,115,22,0.15)", color: "#f97316" };
    case "green":
      return { bg: "rgba(16,185,129,0.15)", color: "#10b981" };
    case "red":
      return { bg: "rgba(239,68,68,0.15)", color: "#ef4444" };
    case "yellow":
      return { bg: "rgba(234,179,8,0.18)", color: "#eab308" };
    case "blue":
      return { bg: "rgba(59,130,246,0.15)", color: "#3b82f6" };
    default:
      return { bg: "color-mix(in srgb, currentColor 12%, transparent)", color: "currentColor" };
  }
}

function bellIconSvg(): string {
  return `<svg class="cobuntu-notif-trigger__icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
  </svg>`;
}

// ─── Drawer (in-place notifications panel) ────────────────────────
//
// Mirrors the auth widget's profile drawer pattern. Built once and
// appended to document.body; toggled via class. Works on community-
// app + on any landing page that mounts the widget — no React
// dependency. v1 renders empty states until the notifications API
// is wired into the widget; even so, an empty drawer beats the
// "click does nothing / page refresh" symptom this fixes.

function ensureDrawer(): void {
  if (drawerEl && backdropEl) return;

  backdropEl = document.createElement("div");
  backdropEl.className = "cobuntu-notif-backdrop";
  backdropEl.addEventListener("click", closeDrawer);

  drawerEl = document.createElement("aside");
  drawerEl.className = "cobuntu-notif-drawer";
  drawerEl.setAttribute("role", "dialog");
  drawerEl.setAttribute("aria-modal", "true");
  drawerEl.setAttribute("aria-label", "Notifications");
  drawerEl.tabIndex = -1;

  document.body.appendChild(backdropEl);
  document.body.appendChild(drawerEl);

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && drawerEl?.classList.contains("cobuntu-notif-drawer--open")) {
      closeDrawer();
    }
  });
}

function openDrawer(): void {
  ensureDrawer();
  renderDrawer();
  requestAnimationFrame(() => {
    backdropEl!.classList.add("cobuntu-notif-backdrop--open");
    drawerEl!.classList.add("cobuntu-notif-drawer--open");
    drawerEl!.focus();
  });

  // Fetch + render real data. We mark-as-read once results land so the
  // badge clears for the user as soon as they've seen the list.
  if (isFetching) return;
  isFetching = true;
  Promise.all([fetchNotificationsList(), fetchFriendRequests()])
    .then(([notifList, reqList]) => {
      notifications = notifList;
      requests = reqList;
      if (drawerEl?.classList.contains("cobuntu-notif-drawer--open")) renderDrawer();
      if (notifList.some((n) => n.status === "UNREAD")) {
        void markAllRead().then(() => {
          if (drawerEl?.classList.contains("cobuntu-notif-drawer--open")) renderDrawer();
        });
      }
    })
    .finally(() => {
      isFetching = false;
    });
}

function closeDrawer(): void {
  if (!drawerEl || !backdropEl) return;
  backdropEl.classList.remove("cobuntu-notif-backdrop--open");
  drawerEl.classList.remove("cobuntu-notif-drawer--open");
}

function renderDrawer(): void {
  if (!drawerEl) return;

  // Group POST_* notifications by post, then render with time-section
  // headers ("Today", "Yesterday", "This Week", …). Mirrors the legacy
  // NotificationsPanel.tsx layout exactly.
  const grouped = groupNotifications(notifications);
  const requestsBadge = requests.length > 0
    ? `<span class="cobuntu-notif-drawer__tab-badge">${requests.length}</span>`
    : "";

  let activityContent: string;
  if (grouped.length === 0) {
    activityContent = emptyStateHtml(
      "activity",
      "No activity yet",
      "When you get notifications, they’ll show up here.",
    );
  } else {
    const parts: string[] = [];
    let currentSection = "";
    for (const item of grouped) {
      const iso = isGroup(item) ? item.createdAt : item.createdAt;
      const section = getTimeSection(iso);
      if (section !== currentSection) {
        parts.push(`<h3 class="cobuntu-notif-drawer__section">${escapeHtml(section)}</h3>`);
        currentSection = section;
      }
      parts.push(renderNotifRow(formatItem(item)));
    }
    activityContent = `<div class="cobuntu-notif-drawer__list">${parts.join("")}</div>`;
  }

  const requestsContent = requests.length > 0
    ? `<div class="cobuntu-notif-drawer__list">${requests.map(renderRequestRow).join("")}</div>`
    : emptyStateHtml(
        "requests",
        "No requests",
        "Friend and membership requests will appear here.",
      );

  drawerEl.innerHTML = `
    <header class="cobuntu-notif-drawer__header">
      <span class="cobuntu-notif-drawer__title">${esc(t("notifications", "Notifications"))}</span>
      <button type="button" class="cobuntu-notif-drawer__close" aria-label="${esc(t("close", "Close notifications"))}">
        <span class="cobuntu-notif-drawer__close-line cobuntu-notif-drawer__close-line--top"></span>
        <span class="cobuntu-notif-drawer__close-line cobuntu-notif-drawer__close-line--bottom"></span>
      </button>
    </header>
    <div class="cobuntu-notif-drawer__tabs" role="tablist">
      <button type="button" role="tab" data-tab="activity" class="cobuntu-notif-drawer__tab ${activeTab === "activity" ? "cobuntu-notif-drawer__tab--active" : ""}">
        Activity
      </button>
      <button type="button" role="tab" data-tab="requests" class="cobuntu-notif-drawer__tab ${activeTab === "requests" ? "cobuntu-notif-drawer__tab--active" : ""}">
        Requests${requestsBadge}
      </button>
    </div>
    <div class="cobuntu-notif-drawer__content">
      ${activeTab === "activity" ? activityContent : requestsContent}
    </div>
  `;

  drawerEl.querySelector(".cobuntu-notif-drawer__close")?.addEventListener("click", closeDrawer);
  drawerEl.querySelectorAll<HTMLButtonElement>(".cobuntu-notif-drawer__tab").forEach((btn) => {
    btn.addEventListener("click", () => {
      const tab = btn.getAttribute("data-tab") as "activity" | "requests";
      if (tab && tab !== activeTab) {
        activeTab = tab;
        renderDrawer();
      }
    });
  });

  // Activity row clicks navigate to the formatter-derived href.
  drawerEl.querySelectorAll<HTMLElement>(".cobuntu-notif-drawer__row[data-href]").forEach((row) => {
    row.addEventListener("click", () => {
      const href = row.getAttribute("data-href");
      if (href) location.href = href;
    });
  });

  // Requests row Accept / Reject buttons.
  drawerEl.querySelectorAll<HTMLButtonElement>("[data-action]").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const action = btn.getAttribute("data-action");
      const usertag = btn.getAttribute("data-usertag");
      if (!usertag) return;
      btn.disabled = true;
      btn.textContent = action === "accept" ? "Accepting…" : "Rejecting…";
      const ok =
        action === "accept"
          ? await acceptFriendRequest(usertag)
          : await rejectFriendRequest(usertag);
      if (ok) {
        requests = requests.filter((r) => r.user.usertag !== usertag);
        renderDrawer();
      } else {
        btn.disabled = false;
        btn.textContent = action === "accept" ? "Accept" : "Reject";
      }
    });
  });
}

function emptyStateHtml(kind: "activity" | "requests", title: string, sub: string): string {
  const icon = kind === "activity"
    ? `<path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 01-3.46 0"></path>`
    : `<path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 00-3-3.87"></path><path d="M16 3.13a4 4 0 010 7.75"></path>`;
  return `<div class="cobuntu-notif-drawer__empty">
    <div class="cobuntu-notif-drawer__empty-icon">
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">${icon}</svg>
    </div>
    <p class="cobuntu-notif-drawer__empty-title">${escapeHtml(title)}</p>
    <p class="cobuntu-notif-drawer__empty-sub">${escapeHtml(sub)}</p>
  </div>`;
}

/** Render a single avatar (image or initial). */
function renderSingleAvatar(a: ActorView, size: number, fontSize: number): string {
  if (a.imageUrl) {
    return `<img src="${escapeAttr(a.imageUrl)}" alt="" style="width:${size}px;height:${size}px;border-radius:9999px;object-fit:cover;display:block;"/>`;
  }
  return `<span class="cobuntu-notif-row__avatar-initial" style="width:${size}px;height:${size}px;font-size:${fontSize}px;display:flex;align-items:center;justify-content:center;border-radius:9999px;background:var(--cobuntu-drawer-hover, color-mix(in srgb, currentColor 12%, transparent));">${escapeHtml(a.initial)}</span>`;
}

/** Render the left-side leading visual: stacked avatars OR solid icon. */
function renderLeading(n: DisplayNotification): string {
  if (n.solidIcon) {
    const tone = solidIconToneStyles(n.solidTone);
    return `<div class="cobuntu-notif-row__icon" style="background:${tone.bg};color:${tone.color};">
      ${solidIconSvg(n.solidIcon)}
    </div>`;
  }
  if (n.actors.length === 0) {
    return `<div class="cobuntu-notif-row__icon" style="background:var(--cobuntu-drawer-hover, color-mix(in srgb, currentColor 12%, transparent));"></div>`;
  }
  if (n.actors.length >= 2) {
    // Stacked (overlapping) avatars for grouped notifications.
    return `<div class="cobuntu-notif-row__avatar-wrap cobuntu-notif-row__avatar-wrap--stack">
      <div class="cobuntu-notif-row__avatar cobuntu-notif-row__avatar--small cobuntu-notif-row__avatar--stack-front">${renderSingleAvatar(n.actors[0], 26, 11)}</div>
      <div class="cobuntu-notif-row__avatar cobuntu-notif-row__avatar--small cobuntu-notif-row__avatar--stack-back">${renderSingleAvatar(n.actors[1], 26, 11)}</div>
      ${n.overlay ? `<span class="cobuntu-notif-row__overlay" style="background:${overlayBg(n.overlay)};">${overlaySvg(n.overlay)}</span>` : ""}
    </div>`;
  }
  return `<div class="cobuntu-notif-row__avatar-wrap">
    <div class="cobuntu-notif-row__avatar">${renderSingleAvatar(n.actors[0], 40, 14)}</div>
    ${n.overlay ? `<span class="cobuntu-notif-row__overlay" style="background:${overlayBg(n.overlay)};">${overlaySvg(n.overlay)}</span>` : ""}
  </div>`;
}

function renderNotifRow(n: DisplayNotification): string {
  const leading = renderLeading(n);
  const snippet = n.snippet
    ? `<p class="cobuntu-notif-row__snippet">“${escapeHtml(n.snippet)}”</p>`
    : "";
  const thumb = n.thumbnail
    ? `<img src="${escapeAttr(n.thumbnail)}" alt="" class="cobuntu-notif-row__thumb"/>`
    : "";
  const clickable = n.href ? `data-href="${escapeAttr(n.href)}" tabindex="0" role="button"` : "";
  return `<div class="cobuntu-notif-drawer__row ${n.isUnread ? "cobuntu-notif-drawer__row--unread" : ""}" ${clickable}>
    ${leading}
    <div class="cobuntu-notif-row__body">
      <p class="cobuntu-notif-row__msg">${n.messageHtml}</p>
      ${snippet}
      <p class="cobuntu-notif-row__time">${escapeHtml(formatTimeAgo(n.createdAt))}</p>
    </div>
    ${thumb}
    ${n.isUnread ? `<span class="cobuntu-notif-row__dot" aria-hidden="true"></span>` : ""}
  </div>`;
}

function renderRequestRow(r: FriendRequestRecord): string {
  const view = actorToView({
    name: r.user.name,
    usertag: r.user.usertag,
    avatarUrl: r.user.profileImage || undefined,
  });
  return `<div class="cobuntu-notif-drawer__row">
    <div class="cobuntu-notif-row__avatar-wrap">
      <div class="cobuntu-notif-row__avatar">${renderSingleAvatar(view, 40, 14)}</div>
    </div>
    <div class="cobuntu-notif-row__body">
      <p class="cobuntu-notif-row__msg"><strong>${escapeHtml(r.user.name)}</strong> <span class="cobuntu-notif-row__muted">wants to be your friend.</span></p>
      <p class="cobuntu-notif-row__time">${escapeHtml(formatTimeAgo(r.sentAt))}</p>
      <div class="cobuntu-notif-row__actions">
        <button type="button" data-action="accept" data-usertag="${escapeAttr(r.user.usertag)}" class="cobuntu-notif-row__btn cobuntu-notif-row__btn--primary">${esc(t("accept", "Accept"))}</button>
        <button type="button" data-action="reject" data-usertag="${escapeAttr(r.user.usertag)}" class="cobuntu-notif-row__btn">${esc(t("reject", "Reject"))}</button>
      </div>
    </div>
  </div>`;
}

function escapeAttr(s: string): string {
  return s.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

// ─── Styles ───────────────────────────────────────────────────────

const CSS = `
.cobuntu-notif-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: var(--cobuntu-bg, transparent);
  color: var(--cobuntu-color, currentColor);
  border: var(--cobuntu-border, 0);
  border-radius: var(--cobuntu-radius, 0);
  padding: var(--cobuntu-padding, 0);
  cursor: pointer;
  font: inherit;
  font-family: var(--cobuntu-font, inherit);
  font-size: var(--cobuntu-font-size, 13px);
  font-weight: var(--cobuntu-font-weight, 500);
  letter-spacing: var(--cobuntu-letter-spacing, normal);
  text-transform: var(--cobuntu-text-transform, none);
  transition: opacity 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.cobuntu-notif-trigger--icon { width: 36px; height: 36px; padding: 0; }
.cobuntu-notif-trigger--button { padding: var(--cobuntu-padding, 8px 16px); }
.cobuntu-notif-trigger--sm.cobuntu-notif-trigger--icon { width: 32px; height: 32px; }
.cobuntu-notif-trigger--lg.cobuntu-notif-trigger--icon { width: 40px; height: 40px; }

.cobuntu-notif-trigger:hover {
  color: var(--cobuntu-hover-color, var(--cobuntu-color, currentColor));
  background: var(--cobuntu-hover-bg, color-mix(in srgb, currentColor 10%, transparent));
  border-radius: 9999px;
}

.cobuntu-notif-trigger__badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9999px;
  background: var(--cobuntu-badge-bg, #d4af37);
  color: var(--cobuntu-badge-text, #000);
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  pointer-events: none;
  box-sizing: border-box;
}
.cobuntu-notif-trigger__badge[hidden] { display: none; }

/* Anon variant — visually muted but still clickable (routes to login).
   See messages widget for the same treatment + rationale. */
.cobuntu-notif-trigger--anon {
  opacity: var(--cobuntu-anon-opacity, 0.4);
}
.cobuntu-notif-trigger--anon:hover {
  opacity: var(--cobuntu-anon-hover-opacity, 0.55);
}

/* ─── Drawer ─────────────────────────────────────────────────── */
.cobuntu-notif-backdrop {
  position: fixed;
  inset: 0;
  background: var(--cobuntu-backdrop, rgba(0, 0, 0, 0.5));
  z-index: 80;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease;
}
.cobuntu-notif-backdrop--open {
  opacity: 1;
  visibility: visible;
}

.cobuntu-notif-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 380px;
  background: var(--cobuntu-drawer-bg, var(--cobuntu-bg, #ffffff));
  /* Text colour. Two theme providers feed this widget and they disagree on
     the var name: the community-app / apex path (lib/widget-theme.ts +
     WidgetThemeStyle) sets --cobuntu-drawer-text (same as the account + cart
     drawers), while this widget's OWN self-fetch path sets
     --cobuntu-drawer-color. Read BOTH so the drawer is legible whichever path
     applied the theme — otherwise on a dark community (Bela Escala) the
     community-app path leaves this unset and it falls back to a dark colour,
     rendering dark-on-dark (prod bug 2026-07-06). The old dark fallback stays
     last for landings that haven't loaded any theme yet. */
  color: var(--cobuntu-drawer-text, var(--cobuntu-drawer-color, var(--cobuntu-color, #1a1a1a)));
  z-index: 85;
  display: flex;
  flex-direction: column;
  font-family: var(--cobuntu-font, inherit);
  /* Match the account drawer (auth.ts): rounded only on the left edge + the
     same soft left-cast shadow. */
  overflow: hidden;
  transform: translateX(100%);
  transition: transform 0.25s cubic-bezier(0.32, 0.72, 0, 1);
  outline: none;
  box-shadow: -20px 0 50px -12px rgba(0, 0, 0, 0.28);
}
/* Mobile: full-screen drawer so the notifications list gets the whole
   viewport. The 400px max-width works for tablets+ but the side
   shadow + sliver of background look weird on phones (Bela 2026-05-31). */
@media (max-width: 639px) {
  .cobuntu-notif-drawer {
    max-width: 100vw;
    box-shadow: none;
    /* Full-bleed sheet on phones — drop the inner-edge radius so the corners
       don't read as a rendering artefact. */
    border-radius: 0;
  }
}
.cobuntu-notif-drawer--open {
  transform: translateX(0);
}

/* Header matches auth + cart drawers — title left, circular muted X-close
   right, 16px padding. Soft hairline (was a hard 15% line that read as "old
   web"); the segmented toggle sits right below with its own inset track, so
   the header only needs the faintest separation. */
.cobuntu-notif-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--cobuntu-drawer-border, color-mix(in srgb, currentColor 9%, transparent));
  flex-shrink: 0;
}
.cobuntu-notif-drawer__title {
  font-size: 16px;
  font-weight: 600;
}
.cobuntu-notif-drawer__close {
  background: color-mix(in srgb, currentColor 6%, transparent);
  border: 0;
  cursor: pointer;
  color: inherit;
  width: 34px;
  height: 34px;
  border-radius: 9999px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.15s ease;
}
.cobuntu-notif-drawer__close:hover { background: color-mix(in srgb, currentColor 12%, transparent); }
.cobuntu-notif-drawer__close-line {
  display: block;
  width: 15px;
  height: 1.5px;
  border-radius: 2px;
  background: currentColor;
  transition: transform 0.3s ease;
  transform-origin: center;
}
.cobuntu-notif-drawer__close-line--top { transform: translateY(3.25px) rotate(45deg); }
.cobuntu-notif-drawer__close-line--bottom { transform: translateY(-3.25px) rotate(-45deg); }

/* Segmented control (Activity / Requests) — an inset muted track with the
   active segment lifted as a small raised "card" (panel-bg fill + soft shadow
   + hairline ring). Replaces the old uppercase underline tabs, which read as a
   dated nav bar. No gradients, no brand accent — just elevation + weight. */
.cobuntu-notif-drawer__tabs {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
  margin: 12px 16px 6px;
  padding: 4px;
  background: color-mix(in srgb, currentColor 5%, transparent);
  border-radius: 12px;
}
.cobuntu-notif-drawer__tab {
  flex: 1;
  height: 34px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  opacity: 0.55;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.18s ease, opacity 0.18s ease, box-shadow 0.18s ease;
}
.cobuntu-notif-drawer__tab:hover { opacity: 0.85; }
.cobuntu-notif-drawer__tab--active {
  opacity: 1;
  background: var(--cobuntu-drawer-bg, var(--cobuntu-bg, #fff));
  box-shadow:
    0 1px 3px -1px rgba(0, 0, 0, 0.18),
    0 0 0 1px color-mix(in srgb, currentColor 6%, transparent);
}
.cobuntu-notif-drawer__tab--active:hover { opacity: 1; }

.cobuntu-notif-drawer__content {
  flex: 1;
  overflow-y: auto;
}

.cobuntu-notif-drawer__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 64px 16px;
  gap: 12px;
}
.cobuntu-notif-drawer__empty-icon {
  height: 64px;
  width: 64px;
  border-radius: 9999px;
  background: var(--cobuntu-drawer-hover, color-mix(in srgb, currentColor 6%, transparent));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.3;
}
.cobuntu-notif-drawer__empty-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}
.cobuntu-notif-drawer__empty-sub {
  font-size: 13px;
  margin: 0;
  opacity: 0.4;
}

/* ─── Rows (notifications + requests) ───────────────────────── */
/* Time-section headers ("Today", "Yesterday", etc.) */
.cobuntu-notif-drawer__section {
  margin: 0;
  padding: 16px 16px 4px;
  font-size: 13px;
  font-weight: 600;
}

.cobuntu-notif-drawer__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

/* Muted (subdued) text inside the message line. */
.cobuntu-notif-row__muted {
  opacity: 0.6;
}

/* Solid-color icon used INSTEAD of avatars (listings, system events). */
.cobuntu-notif-row__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Right-side post thumbnail (44×44 rounded square). */
.cobuntu-notif-row__thumb {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  background: var(--cobuntu-drawer-hover, color-mix(in srgb, currentColor 8%, transparent));
}

/* Stacked avatars (grouped notifications). */
.cobuntu-notif-row__avatar-wrap--stack {
  width: 40px;
  height: 40px;
}
.cobuntu-notif-row__avatar--small {
  height: 26px;
  width: 26px;
  font-size: 11px;
}
.cobuntu-notif-row__avatar--stack-front {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  box-shadow: 0 0 0 2px var(--cobuntu-drawer-bg, var(--cobuntu-bg, #fff));
}
.cobuntu-notif-row__avatar--stack-back {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 1;
  box-shadow: 0 0 0 2px var(--cobuntu-drawer-bg, var(--cobuntu-bg, #fff));
}
.cobuntu-notif-drawer__row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--cobuntu-drawer-border, color-mix(in srgb, currentColor 6%, transparent));
  transition: background 0.15s ease;
  cursor: default;
}
.cobuntu-notif-drawer__row[data-href] { cursor: pointer; }
.cobuntu-notif-drawer__row[data-href]:hover {
  background: var(--cobuntu-drawer-hover, color-mix(in srgb, currentColor 4%, transparent));
}
.cobuntu-notif-drawer__row--unread {
  /* Faint brand wash (was a hardcoded gold) so unread reads as the
     community's colour, matching the brand unread dot. */
  background: color-mix(in srgb, var(--cobuntu-badge-bg, #d4af37) 6%, transparent);
}

.cobuntu-notif-row__avatar-wrap {
  position: relative;
  flex-shrink: 0;
}
.cobuntu-notif-row__avatar {
  height: 40px;
  width: 40px;
  border-radius: 9999px;
  overflow: hidden;
  background: var(--cobuntu-drawer-hover, color-mix(in srgb, currentColor 8%, transparent));
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  font-weight: 600;
  font-size: 14px;
}
.cobuntu-notif-row__overlay {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 17px;
  height: 17px;
  border-radius: 9999px;
  background: var(--cobuntu-drawer-bg, var(--cobuntu-bg, #fff));
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px var(--cobuntu-drawer-bg, var(--cobuntu-bg, #fff));
}
.cobuntu-notif-row__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cobuntu-notif-row__avatar-initial {
  opacity: 0.7;
}

.cobuntu-notif-row__body {
  flex: 1;
  min-width: 0;
}
.cobuntu-notif-row__msg {
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
  word-break: break-word;
}
.cobuntu-notif-row__snippet {
  font-size: 13px;
  margin: 4px 0 0;
  opacity: 0.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}
.cobuntu-notif-row__time {
  font-size: 12px;
  margin: 4px 0 0;
  opacity: 0.35;
}

.cobuntu-notif-row__dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: var(--cobuntu-badge-bg, #d4af37);
  flex-shrink: 0;
  margin-top: 6px;
}

.cobuntu-notif-row__actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.cobuntu-notif-row__btn {
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: var(--cobuntu-button-radius, 6px);
  border: 1px solid var(--cobuntu-drawer-border, color-mix(in srgb, currentColor 20%, transparent));
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-family: inherit;
}
.cobuntu-notif-row__btn:hover:not(:disabled) {
  background: var(--cobuntu-drawer-hover, color-mix(in srgb, currentColor 6%, transparent));
}
.cobuntu-notif-row__btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.cobuntu-notif-row__btn--primary {
  background: var(--cobuntu-badge-bg, #d4af37);
  color: var(--cobuntu-badge-text, #000);
  border-color: transparent;
}
.cobuntu-notif-row__btn--primary:hover:not(:disabled) {
  opacity: 0.9;
  background: var(--cobuntu-badge-bg, #d4af37);
}

.cobuntu-notif-drawer__tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 6px;
  border-radius: 9999px;
  background: var(--cobuntu-badge-bg, #d4af37);
  color: var(--cobuntu-badge-text, #000);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}
`;

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

// ─── Community theme injection ────────────────────────────────────
//
// On Cobuntu's community-app, <WidgetThemeStyle> mounts a <style> tag
// that sets `--cobuntu-bg`, `--cobuntu-color`, `--cobuntu-badge-bg`,
// `--cobuntu-button-radius`, etc. on `:root` — those CSS vars drive
// every widget's visual theme. Customer landings (Lovable, Squarespace,
// Webflow, etc.) DON'T mount that React component, so the widgets fall
// back to defaults and end up looking out of brand with the community.
//
// To close that gap, the widget bundle itself fetches the community
// theme by hostname on init and injects the same `:root` vars. After
// this runs, EVERY widget on the page (auth / cart / messages /
// notifications) reads them — same brand on community-app AND on the
// customer's landing.
//
// Idempotent: STYLE_THEME_ID prevents a duplicate <style> if two
// widgets boot in the same page.

const STYLE_THEME_ID = "cobuntu-community-theme-vars";

interface ThemeConfig {
  bgColor?: string;
  textColor?: string;
  brandColor?: string;
  accentColor?: string;
  linkColor?: string;
  cardRadius?: string;
  inputRadius?: string;
  buttonRadius?: string;
  primaryBtnBg?: string;
  primaryBtnText?: string;
  primaryBtnBorder?: string;
  secondaryBtnBg?: string;
  secondaryBtnText?: string;
  secondaryBtnBorder?: string;
  bodyFont?: string;
  headingFont?: string;
}

async function fetchAndApplyTheme(): Promise<void> {
  if (typeof document === "undefined") return;
  // Skip if another widget (or community-app) already injected theme vars.
  if (document.getElementById(STYLE_THEME_ID)) return;
  if (document.getElementById("cobuntu-widget-theme-style")) return; // WidgetThemeStyle React component
  const host = location.hostname;
  if (!host || host === "localhost") return;
  try {
    const res = await fetch(
      `${API_URL}/api/communities/by-domain/${encodeURIComponent(host)}`,
      { credentials: "omit" },
    );
    if (!res.ok) return;
    const body = (await res.json()) as { themeConfig?: ThemeConfig; brandColor?: string };
    const t = body.themeConfig || {};
    const brand = t.brandColor || (body as { brandColor?: string }).brandColor;
    // Map theme fields → CSS vars. BRAND COLOURS ONLY — we do NOT set the
    // icon-trigger shape (--cobuntu-bg / -border / -radius). The widgets
    // default to a flat, transparent chrome; any shape is the landing's
    // own custom CSS. (Setting --cobuntu-bg to the page bg used to put a
    // coloured box behind every icon — gone.)
    const vars: Record<string, string | undefined> = {
      "--cobuntu-badge-bg": brand,
      "--cobuntu-badge-text": t.primaryBtnText || "#000",
      "--cobuntu-accent": brand,
      "--cobuntu-hover-color": brand,
      "--cobuntu-input-radius": t.inputRadius,
      "--cobuntu-card-radius": t.cardRadius,
      // The community's BUTTON shape, for the widgets' text buttons. It was
      // declared on ThemeConfig above and then never mapped, so inputs and
      // cards followed the community while every widget button stayed at
      // whatever it hardcoded. Note this is NOT `--cobuntu-radius`, the
      // icon-trigger shape the comment above deliberately leaves alone.
      "--cobuntu-button-radius": t.buttonRadius,
      "--cobuntu-font": t.bodyFont,
      "--cobuntu-heading-font": t.headingFont,
      "--cobuntu-link-color": t.linkColor,
      // Drawer panels match the brand bg/text. Expose BOTH text-var names —
      // `--cobuntu-drawer-text` is the canonical one the account + cart drawers
      // (and lib/widget-theme.ts) use; `--cobuntu-drawer-color` is this widget's
      // legacy name, kept for back-compat with any landing CSS that set it.
      "--cobuntu-drawer-bg": t.bgColor,
      "--cobuntu-drawer-text": t.textColor,
      "--cobuntu-drawer-color": t.textColor,
      // Primary button styling (used by Accept buttons in the drawer).
      "--cobuntu-primary-btn-bg": t.primaryBtnBg,
      "--cobuntu-primary-btn-text": t.primaryBtnText,
    };
    const css = Object.entries(vars)
      .filter(([, v]) => !!v)
      .map(([k, v]) => `  ${k}: ${v};`)
      .join("\n");
    if (!css) return;
    const el = document.createElement("style");
    el.id = STYLE_THEME_ID;
    // Wrap in a cascade @layer so the auto-fetched theme is a DEFAULT
    // the customer's own (unlayered) --cobuntu-* CSS always overrides.
    // Unlayered rules beat any layer regardless of source order, so a
    // landing that hand-sets these vars wins — the documented contract.
    el.textContent = `@layer cobuntu-preset {\n:root {\n${css}\n}\n}`;
    document.head.appendChild(el);
  } catch {
    /* non-fatal — widget falls back to its defaults */
  }
}

// ─── Boot ─────────────────────────────────────────────────────────

function init(): void {
  injectStyles();
  // Theme fetch runs in parallel; widget still renders if it fails.
  void fetchAndApplyTheme();
  mountAll();
  watchDom();
  startPolling();
  // Live count from the community-app's React notifications-unread context
  // (same tab), dispatched whenever its count changes (poll, focus, or a live
  // socket `notification:new`). Updates the bell badge instantly; the 60s poll
  // stays as the fallback. See use-notifications-unread.tsx.
  window.addEventListener("cobuntu:notifications-unread", (e) => {
    const c = (e as CustomEvent<{ count?: number }>).detail?.count;
    if (typeof c === "number" && c !== unreadCount) {
      unreadCount = c;
      updateAllBadges();
    }
  });
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}

// ─── Public API ───────────────────────────────────────────────────

declare global {
  interface Window {
    CobuntuNotifications?: {
      unread: () => number;
      refresh: () => Promise<void>;
      open: () => void;
      close: () => void;
    };
  }
}

if (typeof window !== "undefined") {
  window.CobuntuNotifications = {
    unread: () => unreadCount,
    refresh,
    open: navigateToNotifications,
    close: closeDrawer,
  };
}

// ─── Test exports ─────────────────────────────────────────────────

export const __test = {
  isAuthenticated,
  escapeHtml,
  safeAppUrl,
  formatNotification,
  COOKIE_NAME,
  MOUNT_SELECTOR,
  NAV_TARGET,
};
